/**
 * hair-categories.js
 * Dots de paginación para el slider-component nativo de la sección
 * "Categorías circulares (cabello)". Auto-init por IntersectionObserver,
 * scoped a esta sección.
 */
if (!customElements.get('hair-categories-dots')) {
  class HairCategoriesDots extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;

      this._io = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0].isIntersecting) return;
          obs.disconnect();
          this.init();
        },
        // rootMargin amplio: la sección suele ir bajo el pliegue, así que
        // se inicializa con buen margen de anticipación (mientras el
        // usuario todavía está haciendo scroll hacia ella). Con un margen
        // corto, los dots se construían justo cuando la sección entraba
        // en pantalla y se veía el hueco vacío un instante — percibido
        // como demora/parpadeo poco profesional.
        { rootMargin: '600px' }
      );
      this._io.observe(this);

      this._fallbackTimer = setTimeout(() => {
        if (!this._initialized) this.init();
      }, 4000);
    }

    disconnectedCallback() {
      this._io?.disconnect();
      clearTimeout(this._fallbackTimer);
      this._mo?.disconnect();
    }

    init() {
      if (this._initialized) return;
      this._initialized = true;
      clearTimeout(this._fallbackTimer);

      const section = document.getElementById('HairCategories-' + this.sectionId);
      if (!section) return;

      const slider = section.querySelector('slider-component');
      const slideList = document.getElementById('Slider-' + this.sectionId);
      const dotsWrap = section.querySelector('.fs-hair-cat__dots');
      if (!slider || !slideList || !dotsWrap) return;

      let slides = Array.from(slideList.querySelectorAll('.fs-hair-cat__item'));
      if (slides.length === 0) return;

      // Cuántas tarjetas caben completas por vista (no fracciones), según
      // el ancho real disponible. Con esto los dots representan páginas
      // de scroll, no tarjetas individuales — evita el dot "sobrante"
      // que aparecía al contar una tarjeta por dot en vez de una página.
      const getItemsPerView = () => {
        if (!slides.length) return 1;
        const itemWidth = slides[0].getBoundingClientRect().width;
        const gap = parseFloat(getComputedStyle(slideList).gap) || 0;
        const available = slideList.clientWidth;
        const perView = Math.floor((available + gap) / (itemWidth + gap));
        return Math.max(1, perView);
      };

      const getTotalPages = () => Math.ceil(slides.length / getItemsPerView());

      let recentClickPage = null;
      let recentClickTimer = null;

      const setActivePage = (page) => {
        dotsWrap
          .querySelectorAll('.fs-hair-cat__dot')
          .forEach((d, i) => d.classList.toggle('is-active', i === page));
      };

      const updateActive = () => {
        if (recentClickPage !== null) {
          setActivePage(recentClickPage);
          return;
        }
        const current = slideList.querySelector('.fs-hair-cat__item.is-active') || slides[0];
        const idx = slides.indexOf(current);
        if (idx < 0) return;
        const page = Math.floor(idx / getItemsPerView());
        setActivePage(page);
      };

      // Navega usando el mecanismo nativo del slider (scroll-snap real):
      // mueve a la primera tarjeta de la página solicitada.
      const goToPage = (page) => {
        const perView = getItemsPerView();
        const targetIdx = Math.min(page * perView, slides.length - 1);
        const targetSlide = slides[targetIdx];
        if (!targetSlide) return;
        targetSlide.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        setActivePage(page);
        recentClickPage = page;
        clearTimeout(recentClickTimer);
        recentClickTimer = setTimeout(() => {
          recentClickPage = null;
        }, 500);
      };

      const buildDots = () => {
        const totalPages = getTotalPages();

        if (totalPages <= 1) {
          dotsWrap.innerHTML = '';
          dotsWrap.style.display = 'none';
          return;
        }

        const existingDots = dotsWrap.querySelectorAll('.fs-hair-cat__dot');
        dotsWrap.style.display = '';

        // Si el pre-render de Liquid ya tiene el número correcto de dots
        // (caso común: estimación de mobile coincide con el cálculo real),
        // no los reconstruye — solo les vuelve a poner los listeners.
        // Esto evita destruir/recrear DOM que ya se ve bien en pantalla.
        if (existingDots.length === totalPages) {
          existingDots.forEach((dot, page) => {
            dot.onclick = () => goToPage(page);
          });
          return;
        }

        dotsWrap.innerHTML = '';
        for (let page = 0; page < totalPages; page++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'fs-hair-cat__dot' + (page === 0 ? ' is-active' : '');
          dot.setAttribute('aria-label', 'Ir a página ' + (page + 1) + ' de ' + totalPages);
          dot.addEventListener('click', () => goToPage(page));
          dotsWrap.appendChild(dot);
        }
      };

      buildDots();
      updateActive();

      // Sincroniza el dot activo según qué tarjeta está realmente
      // visible al inicio del contenedor. Una sola lectura de layout
      // por evento de scroll, debounced (sin reflow en cada frame).
      let scrollTimer;
      const onScroll = () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          if (recentClickPage !== null) return;
          const listLeft = slideList.getBoundingClientRect().left;
          let closestIdx = 0;
          let closestDist = Infinity;
          slides.forEach((s, i) => {
            const dist = Math.abs(s.getBoundingClientRect().left - listLeft);
            if (dist < closestDist) {
              closestDist = dist;
              closestIdx = i;
            }
          });
          const page = Math.floor(closestIdx / getItemsPerView());
          setActivePage(page);
        }, 80);
      };
      slideList.addEventListener('scroll', onScroll, { passive: true });

      // Recalcula en resize (cambia cuántas tarjetas caben por vista).
      let resizeTimer;
      window.addEventListener(
        'resize',
        () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(buildDots, 200);
        },
        { passive: true }
      );

      // Si el tema reconstruye el slider (theme editor / section render),
      // vuelve a detectar las tarjetas.
      this._mo = new MutationObserver(() => {
        const newSlides = Array.from(slideList.querySelectorAll('.fs-hair-cat__item'));
        if (newSlides.length !== slides.length) {
          slides = newSlides;
          buildDots();
        }
      });
      this._mo.observe(slideList, { childList: true });
    }
  }

  customElements.define('hair-categories-dots', HairCategoriesDots);
}